/**
 * DinarSymbol — official Tunisian Dinar SVG symbol.
 *
 * Exact path from the brand SVG asset (viewBox 0 0 38 40).
 * Width is proportional to height (38 : 40 ratio).
 *
 * Usage:
 *   // Always place BEFORE the number — symbol is on the left.
 *   <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
 *     <DinarSymbol size={18} color={chrome.text} />
 *     <Text>{amount.toFixed(2)}</Text>
 *   </View>
 */
import Svg, {ClipPath, Defs, G, Path, Rect} from 'react-native-svg'

interface Props {
  /** Height of the symbol; width is auto-proportioned (38 : 40). */
  size?: number
  /**
   * The fill for the glyph. Deliberately required: a currency mark inherits the
   * ink of whatever it sits in, and a default would have to name a colour of its
   * own — black is invisible on a dark surface, white is invisible on a light
   * one. Every call site passes a chrome role (`colors.text`, `colors.success`,
   * a category accent), so the default only ever hid the decision.
   */
  color: string
  style?: object
}

export function DinarSymbol({size = 18, color, style}: Props) {
  const width = size * (38 / 40)
  return (
    <Svg
      width={width}
      height={size}
      viewBox="0 0 38 40"
      fill="none"
      style={style}>
      <Defs>
        <ClipPath id="dinar_clip">
          <Rect width="37.1429" height="40" x="0.610352" y="0" />
        </ClipPath>
      </Defs>
      <G clipPath="url(#dinar_clip)">
        <Path
          d="M37.4988 18.4026C37.4988 18.4026 37.7532 18.1605 37.4988 18.4026L37.7532 15.2548C37.7532 15.2548 37.7532 15.0127 37.4988 15.0127H28.5947C28.3403 14.2862 28.0859 13.5598 27.8315 12.8334C25.7963 7.2642 21.4714 2.66354 15.8745 0H15.6201L14.8569 8.95917V9.20132C18.1642 10.1699 21.217 12.3491 23.2522 15.0127H7.98805C7.98805 15.0127 7.73365 15.0127 7.73365 15.2548V18.1605C7.73365 18.1605 7.73364 18.4026 7.98805 18.4026H25.2875C25.7963 19.3712 26.0507 20.3397 26.3051 21.3083H7.98805C7.98805 21.3083 7.73365 21.3083 7.73365 21.5504V24.4561C7.73365 24.4561 7.73364 24.6983 7.98805 24.6983H27.0683C27.3227 26.8775 27.5771 29.2989 27.5771 31.4782C27.5771 31.7203 27.3227 31.9625 27.0683 31.9625H8.24245C5.69841 32.2046 3.40879 30.7518 2.89998 28.3304H1.62797C0.864759 30.5096 0.610352 32.6889 0.610352 34.8681C0.610352 36.321 1.62797 37.7738 2.89998 38.5002C4.4264 39.4688 6.20722 40.1952 7.98805 39.9531H30.3755V31.7203C30.3755 29.0568 30.3755 26.8775 30.1211 24.4561H37.2444C37.2444 24.4561 37.4988 24.4561 37.4988 24.214V21.3083C37.4988 21.3083 37.4988 21.0662 37.2444 21.0662H29.8667C29.6123 20.0976 29.6123 19.129 29.3579 18.1605H37.4988V18.4026Z"
          fill={color}
        />
      </G>
    </Svg>
  )
}
